<template>
  <div id="game-container">
    <canvas ref="gameCanvas" id="game-canvas"></canvas>
    
    <div id="ui-overlay">
      <!-- Main Menu -->
      <MainMenu 
        v-if="!gameStarted && !showTutorial && !showStory" 
        @start-game="handleStartGame"
        @start-tutorial="showTutorial = true"
      />
      
      <!-- Tutorial -->
      <Tutorial 
        v-if="showTutorial"
        @close="showTutorial = false"
      />
      
      <!-- Story Intro / Cutscene -->
      <IntroCutscene 
        v-if="showStory"
        :show="showStory"
        :race="currentRace"
        @close="startGameAfterStory"
      />
      
      <!-- HUD -->
      <GameHUD 
        v-if="gameStarted && !isDead"
        :health="playerHealth"
        :ammo="playerAmmo"
        :kills="kills"
        :isReloading="isReloading"
        :wave="currentWave"
      />
      
      <!-- Touch Controls -->
      <TouchControls 
        v-if="gameStarted && !isDead"
        :showControls="gameStarted && !isDead"
        @move="handleTouchMove"
        @camera-rotate="handleTouchCameraRotate"
        @shoot="handleTouchShoot"
        @reload="handleTouchReload"
      />
      
      <!-- Crosshair -->
      <div v-if="gameStarted && !isDead" id="crosshair"></div>
      
      <!-- Damage indicator -->
      <div v-if="gameStarted && showDamageIndicator" id="damage-indicator"></div>
      
      <!-- Countdown -->
      <div v-if="showCountdown" id="countdown">
        <div class="countdown-number" :class="{ 'pulse': countdownNumber > 0 }">
          {{ countdownText }}
        </div>
      </div>
      
          <!-- Wave Complete -->
          <WaveComplete 
            v-if="showWaveComplete"
            :show="showWaveComplete"
            :wave="currentWave"
          />
          
          <!-- Win Screen -->
          <div v-if="showWinScreen && !gameStarted" id="win-screen">
            <div class="win-content">
              <h1 class="win-title">🎉 JE HEBT GEWONNEN! 🎉</h1>
              <div class="win-messages">
                <p class="win-message">Je hebt de {{ currentDifficulty.toUpperCase() }} moeilijkheidsgraad gewonnen!</p>
                <p v-if="currentRace === 'wizard'" class="win-message special">Je hebt het SECRET WIZARD ras gewonnen!</p>
                <p v-if="currentRace === 'wizard'" class="win-message special">De Wizard is het beste ras en blijft dat voor altijd! ✨</p>
              </div>
              <div class="win-stats">
                <p>Kills: {{ kills }}</p>
                <p>Waves: {{ currentWave }}</p>
              </div>
              <button @click="backToMenu" class="win-button">Terug naar Menu</button>
            </div>
          </div>
          
          <!-- Death Screen -->
          <div v-if="isDead" id="death-screen" @click="respawn">
        <div class="death-content">
          <h1 class="death-title">JE BENT AF!</h1>
          <div class="death-stats">
            <p>Kills: {{ kills }}</p>
          </div>
          <div class="respawn-hint">KLIK OM TE RESPAWNEN</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { Game } from './game/core/Game.js';
import MainMenu from './ui/components/MainMenu.vue';
import GameHUD from './ui/components/GameHUD.vue';
import Tutorial from './ui/components/Tutorial.vue';
import WaveComplete from './ui/components/WaveComplete.vue';
import IntroCutscene from './ui/components/IntroCutscene.vue';
import TouchControls from './ui/components/TouchControls.vue';

const gameCanvas = ref(null);
const gameStarted = ref(false);
const playerHealth = ref(100);
const playerAmmo = ref(30);
const kills = ref(0);
const isReloading = ref(false);
const showDamageIndicator = ref(false);
const showCountdown = ref(false);
const countdownNumber = ref(3);
const countdownText = ref('3');
const isDead = ref(false);
const showTutorial = ref(false);
const showStory = ref(false);
const currentWave = ref(1);
const showWaveComplete = ref(false);
const showWinScreen = ref(false);
let lastHealth = 100;
let currentRace = 'human';
let currentMode = 'single';
let currentDifficulty = 'medium';
let pendingGameStart = null; // Store game start parameters

let game = null;
let countdownInterval = null;

const handleStartGame = (race, mode, difficulty = 'medium') => {
  console.log('=== handleStartGame CALLED ===');
  console.log('Race:', race);
  console.log('Mode:', mode);
  console.log('Difficulty:', difficulty);
  console.log('gameCanvas.value:', gameCanvas.value);
  
  if (!gameCanvas.value) {
    console.error('gameCanvas is null!');
    return;
  }
  
  if (!race) {
    console.error('No race provided!');
    alert('Selecteer eerst een ras!');
    return;
  }
  
  currentRace = race;
  currentMode = mode;
  currentDifficulty = difficulty;
  
  // Store game start parameters
  pendingGameStart = { race, mode, difficulty };
  
  console.log('Setting showStory to true...');
  // Show story intro before starting game
  showStory.value = true;
  console.log('showStory.value is now:', showStory.value);
};

const startGameAfterStory = () => {
  console.log('=== startGameAfterStory CALLED ===');
  console.log('pendingGameStart:', pendingGameStart);
  console.log('gameCanvas.value:', gameCanvas.value);
  
  showStory.value = false;
  
  if (!pendingGameStart || !gameCanvas.value) {
    console.error('Cannot start game: missing pendingGameStart or gameCanvas');
    return;
  }
  
  // Wait a bit to ensure cutscene is fully cleaned up (reduced delay)
  setTimeout(() => {
    const { race, mode, difficulty } = pendingGameStart;
    isDead.value = false;
    
    // Initialize game but don't start it yet
    gameStarted.value = true;
  game = new Game(gameCanvas.value, {
    race: race,
    mode: mode,
    difficulty: difficulty,
    onHealthChange: (health) => { 
      // Show damage indicator if health decreased
      if (health < lastHealth && health > 0) {
        showDamageIndicator.value = true;
        setTimeout(() => {
          showDamageIndicator.value = false;
        }, 200);
      }
      lastHealth = health;
      playerHealth.value = health; 
    },
    onAmmoChange: (ammo) => { playerAmmo.value = ammo; },
    onKill: () => { kills.value++; },
    onReloadChange: (reloading) => { isReloading.value = reloading; },
    onDeath: () => {
      isDead.value = true;
      // Game will be stopped by the Game class
    },
    onWaveChange: (wave, enemies) => {
      currentWave.value = wave;
    },
    onWaveComplete: (wave) => {
      showWaveComplete.value = true;
      setTimeout(() => {
        showWaveComplete.value = false;
      }, 3000); // Hide after 3 seconds
    },
    onGameWin: () => {
      showWinScreen.value = true;
      if (game) game.stop(); // Stop game when won
      
      // Unlock Wizard and Impossible difficulty
      localStorage.setItem('gameWon', 'true');
      localStorage.setItem('wizardUnlocked', 'true');
      localStorage.setItem('impossibleUnlocked', 'true');
      console.log('Game won! Wizard and Impossible unlocked!');
    }
  });
  
  lastHealth = playerHealth.value;
  
  // Initialize game entities (player, enemies, etc.)
  game.initGame();
  
  // Start countdown
  startCountdown();
  }, 100); // 100ms delay to ensure cutscene cleanup (reduced from 200ms)
};

const backToMenu = () => {
  // Clean up current game
  if (game) {
    game.destroy();
    game = null;
  }
  
  // Clear countdown interval
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  
  // Reset all state - this should make menu visible again
  showWinScreen.value = false;
  gameStarted.value = false;
  isDead.value = false;
  playerHealth.value = 100;
  playerAmmo.value = 30;
  kills.value = 0;
  currentWave.value = 1;
  showCountdown.value = false;
  showWaveComplete.value = false;
  showStory.value = false;
  showDamageIndicator.value = false;
  showTutorial.value = false;
  lastHealth = 100;
  pendingGameStart = null;
  
  console.log('Back to menu - Menu should be visible now');
  
  // Trigger unlock check in menu (by dispatching custom event)
  window.dispatchEvent(new CustomEvent('gameUnlocksUpdated'));
};

const respawn = () => {
  // Clean up current game
  if (game) {
    game.destroy();
    game = null;
  }
  
  // Reset state
  isDead.value = false;
  playerHealth.value = 100;
  playerAmmo.value = 30;
  kills.value = 0;
  lastHealth = 100;
  showCountdown.value = false;
  showWaveComplete.value = false;
  currentWave.value = 1;
  
  // Restart game directly (skip story on respawn)
  if (!gameCanvas.value) return;
  
  gameStarted.value = true;
  game = new Game(gameCanvas.value, {
    race: currentRace,
    mode: currentMode,
    difficulty: currentDifficulty,
    onHealthChange: (health) => { 
      if (health < lastHealth && health > 0) {
        showDamageIndicator.value = true;
        setTimeout(() => {
          showDamageIndicator.value = false;
        }, 200);
      }
      lastHealth = health;
      playerHealth.value = health; 
    },
    onAmmoChange: (ammo) => { playerAmmo.value = ammo; },
    onKill: () => { kills.value++; },
    onReloadChange: (reloading) => { isReloading.value = reloading; },
    onDeath: () => {
      isDead.value = true;
    },
    onWaveChange: (wave, enemies) => {
      currentWave.value = wave;
    },
    onWaveComplete: (wave) => {
      showWaveComplete.value = true;
      setTimeout(() => {
        showWaveComplete.value = false;
      }, 3000);
    },
    onGameWin: () => {
      showWinScreen.value = true;
      if (game) game.stop(); // Stop game when won
      
      // Unlock Wizard and Impossible difficulty
      localStorage.setItem('gameWon', 'true');
      localStorage.setItem('wizardUnlocked', 'true');
      localStorage.setItem('impossibleUnlocked', 'true');
      console.log('Game won! Wizard and Impossible unlocked!');
    }
  });
  
  lastHealth = playerHealth.value;
  
  // Initialize game entities
  game.initGame();
  
  // Start countdown
  startCountdown();
};

const startCountdown = () => {
  showCountdown.value = true;
  countdownNumber.value = 3;
  countdownText.value = '3';
  
  countdownInterval = setInterval(() => {
    countdownNumber.value--;
    
    if (countdownNumber.value > 0) {
      countdownText.value = countdownNumber.value.toString();
    } else if (countdownNumber.value === 0) {
      countdownText.value = 'GO!';
    } else {
      // Countdown finished, start the game
      clearInterval(countdownInterval);
      showCountdown.value = false;
      game.start();
    }
  }, 1000);
};

// Touch control handlers
const handleTouchMove = (direction) => {
  if (game && game.player) {
    game.player.setTouchMove(direction.x, direction.y);
  }
};

const handleTouchCameraRotate = (rotation) => {
  if (game && game.player) {
    game.player.setTouchCameraRotate(rotation.deltaX, rotation.deltaY);
  }
};

const handleTouchShoot = (shooting) => {
  if (game && game.player) {
    game.player.setTouchShoot(shooting);
  }
};

const handleTouchReload = () => {
  if (game && game.player) {
    game.player.setTouchReload();
  }
};

onMounted(() => {
  // Game will be initialized when menu emits start-game
  console.log('App mounted, gameCanvas ref:', gameCanvas.value);
  
  // Ensure canvas is available
  if (gameCanvas.value) {
    console.log('Canvas element found:', gameCanvas.value);
  } else {
    console.error('Canvas element not found!');
  }
});

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  if (game) {
    game.destroy();
  }
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Arial', sans-serif;
  background: #000;
  color: #fff;
  overflow: hidden;
}

#game-container {
  width: 100vw;
  height: 100vh;
  position: relative;
}

#game-canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none; /* Prevent default touch behaviors */
}

#ui-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;
}

/* Allow menu and interactive elements to receive pointer events */
#ui-overlay > * {
  pointer-events: auto;
}

#ui-overlay button,
#ui-overlay .game-button,
#ui-overlay .race-button,
#ui-overlay .difficulty-button {
  pointer-events: auto !important;
}

#crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  z-index: 101;
}

#crosshair::before,
#crosshair::after {
  content: '';
  position: absolute;
  background: #fff;
}

#crosshair::before {
  width: 2px;
  height: 8px;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
}

#crosshair::after {
  width: 8px;
  height: 2px;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
}

#damage-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 0, 0, 0.3);
  pointer-events: none;
  z-index: 103;
  animation: damageFlash 0.2s ease-out;
}

@keyframes damageFlash {
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 0;
  }
}

#countdown {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 200;
  pointer-events: none;
}

.countdown-number {
  font-size: 8em;
  font-weight: bold;
  color: #fff;
  text-shadow: 
    0 0 20px rgba(255, 255, 255, 0.8),
    0 0 40px rgba(255, 255, 255, 0.6),
    0 0 60px rgba(255, 255, 255, 0.4);
  text-align: center;
  font-family: 'Arial', sans-serif;
  animation: countdownPulse 0.5s ease-out;
}

.countdown-number.pulse {
  animation: countdownPulse 0.5s ease-out;
}

@keyframes countdownPulse {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

#death-screen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 300;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
}

.death-content {
  text-align: center;
  color: #fff;
}

.death-title {
  font-size: 4em;
  font-weight: bold;
  color: #ff4444;
  text-shadow: 
    0 0 20px rgba(255, 68, 68, 0.8),
    0 0 40px rgba(255, 68, 68, 0.6);
  margin-bottom: 30px;
  animation: deathPulse 2s infinite;
}

.death-stats {
  font-size: 1.5em;
  margin-bottom: 40px;
  color: #aaa;
}

.death-stats p {
  margin: 10px 0;
}

#death-screen {
  cursor: pointer;
}

.respawn-hint {
  font-size: 1.2em;
  color: #ffaa00;
  margin-top: 30px;
  text-transform: uppercase;
  font-weight: bold;
  animation: pulse 1.5s infinite;
  text-shadow: 
    0 0 10px rgba(255, 170, 0, 0.8),
    0 0 20px rgba(255, 170, 0, 0.6);
}

@keyframes deathPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

#win-screen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 400;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.win-content {
  text-align: center;
  padding: 60px;
  background: linear-gradient(135deg, rgba(157, 78, 221, 0.3) 0%, rgba(255, 170, 0, 0.3) 100%);
  border-radius: 20px;
  border: 4px solid #ffaa00;
  box-shadow: 0 0 50px rgba(255, 170, 0, 0.8);
  max-width: 800px;
}

.win-title {
  font-size: 4em;
  color: #ffaa00;
  text-shadow: 
    0 0 30px rgba(255, 170, 0, 0.9),
    0 0 60px rgba(255, 170, 0, 0.7),
    0 0 90px rgba(255, 170, 0, 0.5);
  margin-bottom: 30px;
  animation: winPulse 2s infinite;
}

@keyframes winPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.win-messages {
  margin: 30px 0;
}

.win-message {
  font-size: 1.8em;
  color: #fff;
  margin: 15px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.win-message.special {
  color: #9D4EDD;
  font-size: 2em;
  font-weight: bold;
  text-shadow: 
    0 0 20px rgba(157, 78, 221, 0.8),
    0 0 40px rgba(157, 78, 221, 0.6);
  animation: wizardGlow 2s infinite;
}

@keyframes wizardGlow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.win-stats {
  font-size: 1.5em;
  color: #fff;
  margin: 30px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.win-button {
  padding: 20px 40px;
  background: #8B0000;
  border: 3px solid #A00000;
  color: #fff;
  font-size: 1.5em;
  font-weight: bold;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 30px;
}

.win-button:hover {
  background: #A00000;
  transform: scale(1.1);
  box-shadow: 0 0 30px rgba(139, 0, 0, 0.8);
}

/* Tablet responsive styles */
@media (max-width: 1024px) {
  .countdown-number {
    font-size: 6em;
  }
  
  .win-content {
    padding: 40px 30px;
    max-width: 90%;
  }
  
  .win-title {
    font-size: 3em;
  }
  
  .win-message {
    font-size: 1.5em;
  }
  
  .death-title {
    font-size: 3em;
  }
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .countdown-number {
    font-size: 4em;
  }
  
  .win-content {
    padding: 30px 20px;
  }
  
  .win-title {
    font-size: 2em;
  }
  
  .win-message {
    font-size: 1.2em;
  }
  
  .win-button {
    padding: 15px 30px;
    font-size: 1.2em;
  }
  
  .death-title {
    font-size: 2em;
  }
  
  .death-stats {
    font-size: 1.2em;
  }
}
</style>

